import { Controller } from "@hotwired/stimulus"
import { resolveAnchor, wrapSpan, normalizeQuote, scrollBehavior } from "./text_anchor"

/**
 * Opens a chapter at the passage a question came from and marks it
 * (docs/question-references.md). The link is `?reference=<id>`; the server
 * looks the reference up and hands us its stored anchor, so a long quote
 * never rides in the URL.
 *
 * Same resolve-and-paint code and the same fail-soft rule as student
 * highlights (docs/highlights.md): an anchor that no longer resolves scrolls
 * to the section heading it was under and marks nothing. We never mark a
 * best guess.
 *
 * Marks get their own class (not .text-anchor-mark), so the appraisal quiz's
 * "Review →" repaint, which clears .text-anchor-mark, leaves ours alone.
 */
export default class extends Controller {
  static values = {
    anchor: Object,
    heading: String,
    scope: { type: String, default: "" }
  }

  connect() {
    // After the theme controllers rebuild the DOM (outline, flip cards, quiz),
    // as text-anchor and the highlights layer do.
    requestAnimationFrame(() => requestAnimationFrame(() => this.#apply()))
  }

  #apply() {
    const scope = (this.scopeValue && this.element.querySelector(this.scopeValue)) || this.element
    const found = this.anchorValue?.quote ? resolveAnchor(scope, this.anchorValue) : null

    if (found) {
      const marks = wrapSpan(found, () => {
        const mark = document.createElement("mark")
        mark.className = "source-passage-mark"
        return mark
      })
      if (marks.length) {
        // A book-sections focus link may have folded the passage away.
        marks.forEach((mark) => mark.closest("details:not([open])")?.setAttribute("open", ""))
        marks[0].scrollIntoView({ behavior: scrollBehavior(), block: "center" })
        this.#announce(marks[marks.length - 1], "Jumped to the passage this question is based on.")
        return
      }
    }

    const heading = this.#heading(scope)
    if (heading) {
      heading.closest("details:not([open])")?.setAttribute("open", "")
      heading.scrollIntoView({ behavior: scrollBehavior(), block: "start" })
      this.#announce(heading, "The passage this question was based on has changed. This is the section it was in.")
    }
  }

  #heading(scope) {
    const wanted = normalizeQuote(this.headingValue).toLowerCase()
    if (!wanted) return null

    return [...scope.querySelectorAll("h2, h3, h4")].find((el) => {
      const clone = el.cloneNode(true)
      clone.querySelectorAll(".section-number").forEach((span) => span.remove())
      return normalizeQuote(clone.textContent).toLowerCase() === wanted
    })
  }

  #announce(el, message) {
    const note = document.createElement("span")
    note.className = "visually-hidden"
    note.setAttribute("role", "status")
    note.textContent = message
    el.insertAdjacentElement("afterend", note)
  }
}
